import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { DocumentDownloadSelection, DownloadStatus } from '@app/core/model/document-download';
import { WorkOrderDetailsService } from '@app/work-order/work-order-details.service';

export interface OfflineDownloadState {
  running: boolean;
  metadata: DownloadStatus | null;
  documents: Record<string, DownloadStatus>;
}

export interface OfflineDownloadResult {
  workOrderId: string;
  success: boolean;
}

const IDLE_STATE: OfflineDownloadState = { running: false, metadata: null, documents: {} };

/** Owns download progress so it survives the download dialog being closed and reopened. */
@Injectable({
  providedIn: 'root',
})
export class OfflineDownloadService {
  readonly completed$ = new Subject<OfflineDownloadResult>();
  private readonly states = new Map<string, BehaviorSubject<OfflineDownloadState>>();

  constructor(private workOrderDetailsService: WorkOrderDetailsService) {}

  state$(workOrderId: string): Observable<OfflineDownloadState> {
    return this.getState(workOrderId).asObservable();
  }

  isRunning(workOrderId: string): boolean {
    return this.getState(workOrderId).value.running;
  }

  getStoredSelection(workOrderId: string): Promise<DocumentDownloadSelection | null> {
    return this.workOrderDetailsService.getStoredDocumentSelection(workOrderId);
  }

  async download(workOrderId: string, selection: DocumentDownloadSelection): Promise<boolean> {
    const state = this.getState(workOrderId);
    if (state.value.running) {
      return false;
    }

    state.next({ running: true, metadata: 'downloading', documents: {} });
    const onDocumentStatus = (documentId: string, status: DownloadStatus) => {
      state.next({ ...state.value, documents: { ...state.value.documents, [documentId]: status } });
    };

    let success = false;
    try {
      if (await this.workOrderDetailsService.isWorkOrderDownloaded(workOrderId)) {
        // Clean up stale/incomplete data before downloading a fresh offline snapshot.
        await this.workOrderDetailsService.removeDownloadWorkOrder(workOrderId);
      }
      await this.workOrderDetailsService.downloadWorkOrder(workOrderId, { selection, onDocumentStatus });
      success = true;
    } catch (error) {
      console.warn(`[OFFLINE_DOWNLOAD] Failed to download work order ${workOrderId}`, error);
    } finally {
      state.next({ ...state.value, running: false, metadata: success ? 'done' : 'failed' });
      this.completed$.next({ workOrderId, success });
    }
    return success;
  }

  private getState(workOrderId: string): BehaviorSubject<OfflineDownloadState> {
    let state = this.states.get(workOrderId);
    if (!state) {
      state = new BehaviorSubject<OfflineDownloadState>(IDLE_STATE);
      this.states.set(workOrderId, state);
    }
    return state;
  }
}
